import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import {
  GroupSizes,
  GuestInfo,
  SelectedValues,
  SpecialGroupDetail,
} from "./index.d";
import { useQuery } from "react-query";
import { GuestType, Price, Seat, SeatType } from "@/graphql/types";
import SelectItem from "../common/input/selectItem";
import Button from "../common/button/button";
import { isMobile } from "@/lib/ts/device";
import { SEAT_STATES } from "../seat";
import LoadingIcon from "../common/loadingIcon";
import { getDefaultSeatTypes } from "@/lib/ts/admin/defaultSeatTypes";
import SelectItemV2 from "../common/input/selectItemV2";
import { InputLabel } from "@mui/material";

type Props = {
  guestInfo: GuestInfo;
  specialGroupDetails: SpecialGroupDetail[];
  changedSeats: Seat[];
  seatTypes: SeatType[];
  guestTypes: GuestType[];
  prices: Price[];
  selectedValues: SelectedValues;
  stageId: number | undefined;
  onSubmit: (selectedValues: SelectedValues) => void;
};

const WhoGonnaSitWhere = (props: Props) => {
  const [loading, setLoading] = useState(false)

  const [groupSizes, setGroupSizes] = useState(
    {} as { [type: number]: number },
  );

  const getGuestTypeName = useCallback(
    (code: number) => {
      const targetGuestType = props.specialGroupDetails.find(item => item.type === code)
      return "券種: " + (targetGuestType?.name ?? "一般");
    },
    [props.specialGroupDetails],
  );

  const mergedSeatTypes: SeatType[] = useMemo(
    () => [
      ...getDefaultSeatTypes(props.stageId || -1),
      ...props.seatTypes,
    ],
    [props.seatTypes, props.stageId],
  );


  const changedSeatsOptions = useMemo(() => {
    return props.changedSeats.map((seat) => {
      console.log(mergedSeatTypes);
      const type = mergedSeatTypes?.find((type) => type.id === seat.type);
      return {
        name: `${seat.seatName}(${type?.name})`,
        value: seat.id,
      };
    });
  }, [mergedSeatTypes, props.changedSeats]);

  const [selectedValues, setSelectedValues] = useState(props.selectedValues);
  const onSeatTypeSelect = useCallback(
    (seatId: number, guestType: number, index: number) => {
      // 入力状況の更新
      const selectedValuesCopy = { ...selectedValues };
      if (!selectedValuesCopy[guestType]) {
        selectedValuesCopy[guestType] = {};
      }
      selectedValuesCopy[guestType][index] = seatId;

      setSelectedValues(selectedValuesCopy);
    },
    [selectedValues],
  );

  const availableDetailKeys = useMemo(() => {
    return Object.keys(groupSizes).filter(key => groupSizes[parseInt(key)] > 0)
  }, [groupSizes])


  // 座席種類か観客種類が全員同じならスキップ可能
  const canSkip = useMemo(() => {
    if(availableDetailKeys.length <= 1) {
      return true
    }
    let match: number | undefined = undefined
    return !props.changedSeats.map(s => s.type).some(s => {
      if(match === undefined) {
        match = s
      }
      return match !== s
    })
  }, [availableDetailKeys, props.changedSeats])
  console.log(selectedValues)

  useQuery([canSkip, availableDetailKeys, props.changedSeats], () => {
    if(canSkip) {
      if(availableDetailKeys.length === 0) {
        return
      }
      const newSelectedValues: SelectedValues = {}
      if(availableDetailKeys.length === 1) {
        const guestType = parseInt(availableDetailKeys[0])
        newSelectedValues[guestType] = {}
        props.changedSeats.forEach((s, index) => {
          if(s.id) {
            newSelectedValues[guestType][index + 1] = s.id
          }
        })
        setSelectedValues(newSelectedValues)
      } else {
        const newSelectedValues: SelectedValues = {}
        let count = 0
        Object.keys(groupSizes).forEach((key) => {
          const guestType = parseInt(key)
          const groupSize = groupSizes[guestType]
          for(let index = 1; index <= groupSize; index++) {
            const seat = props.changedSeats[count]
            if(seat && seat.id) {
              if(!newSelectedValues[guestType]) {
                newSelectedValues[guestType] = {}
              }
              newSelectedValues[guestType][index] = seat.id
            }
            count++
          }
        })
        setSelectedValues(newSelectedValues)
      }
    }
  })

  const guest = useCallback(
    (guestType: number, index: number) => {
      const selectedSeatId = selectedValues[guestType]?.[index]
      const selectedIndex = changedSeatsOptions.findIndex(o => o.value === selectedSeatId)
      console.log(selectedValues, changedSeatsOptions)
      console.log(guestType, index)
      return (
      <div className="guest" key={"guest-type-" + guestType + "-" + index}>
        <div className="guest-type">{getGuestTypeName(guestType)}</div>
        <div className="seat-type-select">
          <InputLabel>座席選択</InputLabel>
          <SelectItemV2 
            items={changedSeatsOptions}
            selectedIndex={selectedIndex}
            changeIndex={(selectedItem, _) => {
              onSeatTypeSelect(selectedItem.value, guestType, index);
            }}
            width={250}
          />
        </div>
      </div>
    )},
    [changedSeatsOptions, getGuestTypeName, onSeatTypeSelect, selectedValues],
  );


  const invalidInput = useMemo(() => {
    let invalid = false;
    const countedArray: number[] = [];
    if(Object.keys(selectedValues).flatMap((key) => Object.keys(selectedValues[parseInt(key)])).length !== props.changedSeats.length) {
      console.log("invalid input length")
      return true
    }
    Object.keys(selectedValues).some((guestType) => {
      const value = selectedValues[parseInt(guestType)];
      return Object.keys(value).some((key) => {
        const seatId = value[parseInt(key)];
        if (countedArray.indexOf(seatId) > -1) {
          invalid = true;
          return true;
        } else {
          countedArray.push(seatId);
        }
        return false
      });
    });
    return invalid;
  }, [props.changedSeats.length, selectedValues]);

  const priceDisplay = useMemo(() => {
    // 価格表示の更新
    let price = 0;
    Object.keys(selectedValues).forEach((guestType) => {
      const value = selectedValues[parseInt(guestType)];
      Object.keys(value).forEach((key2) => {
        const seatId = value[parseInt(key2)];

        const seat = props.changedSeats?.find((seat) => seat.id === seatId);
        const seatType = mergedSeatTypes?.find(
          (type) => type.id === seat?.type,
        );
        if (seatType) {
          const plus = props.prices?.find(
            (priceInfo) =>
              priceInfo.seatType === seatType.id &&
              priceInfo.guestType === parseInt(guestType),
          );
          if (plus) {
            price += plus.price;
          }
        }
      });
    });

    return price;
  }, [mergedSeatTypes, props.changedSeats, props.prices, selectedValues]);

  useQuery([], () => {
    const initialGroupSizes: GroupSizes = {};
    initialGroupSizes[0] = props.guestInfo.groupSize;
    props.specialGroupDetails.forEach((detail) => {
      initialGroupSizes[detail.type] = detail.size;
    });

    setGroupSizes(initialGroupSizes);
  });

  const submit = useCallback(() => {
    if (invalidInput) {
      return;
    }
    setLoading(true)
    props.onSubmit(selectedValues);
    setLoading(false)
  }, [invalidInput, selectedValues, props]);

  const component = useMemo(() => {
    return (
      <WhoGonnaSitWhereStyle>
        <div className="header">
          <div className="title">{canSkip ? "座席確認" : "席種割り振り"}</div>
          <div className="description">
            {canSkip ? "「次へ」を押してください。" : "どのお客様が、どの座席を利用されるか選択してください。"}
          </div>
        </div>
        <div className="guests-wrapper">
          {Object.keys(groupSizes).map((type) => {
            const groupSize = groupSizes[parseInt(type)];
            const guests = [];
            for (let i = 1; i <= groupSize; i++) {
              guests.push(guest(parseInt(type), i));
            }
            return (
              <div className="guest-group" key={"guest-seat-type-" + type}>
                {guests}
              </div>
            );
          })}
        </div>
        <div className="priceList">合計: {priceDisplay}円</div>
        <div className="buttons">
          <Button
            disabled={invalidInput}
            buttonType={isMobile() ? "default-red" : "powerful-red"}
            label="次へ"
            onClick={submit}
          />
        </div>
        {loading ? <LoadingIcon /> : <></>}
      </WhoGonnaSitWhereStyle>
    );
  }, [canSkip, groupSizes, guest, invalidInput, loading, priceDisplay, submit]);
  return component;
};

const WhoGonnaSitWhereStyle = styled.div`
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  width: 90%;
  margin: auto;
  padding: 10px 10px 10px 10px;
  color: black;
  .header {
    margin: 0 0 10px 0;
    .title {
      font-weight: bold;
      text-align: center;
      margin: 10px 10px 10px 10px;
    }
  }
  .guests-wrapper {
    width: 300px;
    margin: 10px auto 10px auto;
    .guest-group {
      .guest {
        border-radius: 8px;
        background-color: white;
        color: black;
        padding: 8px 10px 8px 10px;
        margin: 10px auto 10px auto;
        .guest-type {
          font-weight: bold;
          font-size: 16px;
          width: 300px;
          white-space: nowrap;
          overflow-x: hidden;
          text-overflow: ellipsis;
        }
        .seat-type-select {
          text-align: center;
          margin: auto;
        }
      }
    }
  }
  .priceList {
    margin: auto;
    text-align: center;
  }
  .buttons {
    text-align: center;
  }
`;

export default WhoGonnaSitWhere;
